const PDF_EXTENSIONS = new Set(["pdf"]);
const IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg", "heic", "heif"]);

export type DocumentPreviewKind = "pdf" | "image" | "unsupported";

export const DEFAULT_PDF_PREVIEW_ZOOM = 75;

const PDF_PREVIEW_ZOOM_PARAM = "zoom";

export const buildPdfPreviewUrl = (url: string, zoom = DEFAULT_PDF_PREVIEW_ZOOM): string => {
  if (!url.trim()) return url;

  const [base, hash = ""] = url.split("#", 2);
  const params = new URLSearchParams(hash.replace(/^\?/, ""));
  params.set(PDF_PREVIEW_ZOOM_PARAM, String(zoom));

  const nextHash = params.toString();
  return nextHash ? `${base}#${nextHash}` : base;
};

const MIME_TO_EXTENSION: Record<string, string> = {
  "application/pdf": "pdf",
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/png": "png",
  "image/gif": "gif",
  "image/webp": "webp",
  "image/bmp": "bmp",
  "image/svg+xml": "svg",
  "application/msword": "doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
};

const extractExtensionFromPath = (value: string): string => {
  const match = value.match(/\.([a-z0-9]+)(?:\?|#|$)/i);
  return match?.[1]?.toLowerCase() ?? "";
};

const normalizeExtensionValue = (value: string): string => {
  const trimmed = value.trim().toLowerCase().replace(/^\./, "");
  if (!trimmed) return "";

  if (MIME_TO_EXTENSION[trimmed]) return MIME_TO_EXTENSION[trimmed];

  if (trimmed.includes("/")) {
    const subtype = trimmed.split("/").pop() ?? "";
    if (subtype === "jpeg") return "jpg";
    if (PDF_EXTENSIONS.has(subtype) || IMAGE_EXTENSIONS.has(subtype)) return subtype;
  }

  return trimmed;
};

export const resolveFileExtension = (...sources: (string | undefined)[]): string => {
  for (const source of sources) {
    if (!source) continue;

    const normalized = normalizeExtensionValue(source);
    if (!normalized) continue;

    if (PDF_EXTENSIONS.has(normalized) || IMAGE_EXTENSIONS.has(normalized)) {
      return normalized;
    }

    const fromPath = extractExtensionFromPath(source);
    if (fromPath) return fromPath;

    if (normalized.length <= 5) return normalized;
  }

  return "";
};

export const getDocumentPreviewKind = (extension: string): DocumentPreviewKind => {
  if (PDF_EXTENSIONS.has(extension)) return "pdf";
  if (IMAGE_EXTENSIONS.has(extension)) return "image";
  return "unsupported";
};

export const getDocumentTypeLabel = (extension: string): string => {
  if (!extension) return "FILE";
  if (IMAGE_EXTENSIONS.has(extension)) return "IMG";
  if (extension === "pdf") return "PDF";
  if (extension === "doc" || extension === "docx") return "DOC";
  if (extension === "xls" || extension === "xlsx") return "XLS";
  if (extension === "ppt" || extension === "pptx") return "PPT";
  if (extension === "txt") return "TXT";
  if (extension === "zip" || extension === "rar") return "ZIP";

  return extension.toUpperCase();
};
